import { MediaItem } from '../types';
import { imageProcessingService, ProcessedImage, getSourceVersion } from './imageProcessingService';
import { imageStorageService } from './imageStorageService';
import { getThumbnailUrl, calculateThumbnailWidth } from './thumbnailCache';
import { processImageWithQueue, cancelProcessing } from './imageProcessingQueue';
import { convertHeicSingleFlight, cancelActiveHeicJob } from './heicWorkerClient';
import { uploadthingService } from './uploadthingService';
import { captureSentryException } from '../sentry';

const toDropboxRawUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('raw', '1');
    parsed.searchParams.delete('dl');
    return parsed.toString();
  } catch {
    return url;
  }
};

// Map to store Object URLs: ItemID -> BlobURL
const cache = new Map<string, string>();
// Map to store active fetches to allow cancellation: ItemID -> AbortController
const activeFetches = new Map<string, AbortController>();

const MAX_CACHE_SIZE = 5; // Keep previous, current, and next few items

export const mediaCache = {
  /**
   * Gets a Blob URL for the given item. 
   * If cached, returns immediately. 
   * If not, fetches it.
   */
  load: async (item: MediaItem, accessToken: string | null): Promise<string> => {
    if (!item) throw new Error("No item provided");
    
    // 1. Check Cache
    if (cache.has(item.id)) {
      return cache.get(item.id)!;
    }

    // 2. Check if already fetching
    if (activeFetches.has(item.id)) {
      // Return a promise that resolves when the existing fetch completes
      // (Simplified: we just wait for the existing logic to populate cache)
      // For robustness in this demo, we'll just let the new request supersede or wait.
      // Actually, to prevent double-fetching, we should ideally share the promise.
      // For simplicity: we'll cancel the old one and restart, or just proceed. 
      // Let's proceed with a fresh fetch to be safe, cancelling old.
      activeFetches.get(item.id)?.abort();
    }

    const controller = new AbortController();
    activeFetches.set(item.id, controller);

    try {
      let blob: Blob;

      if (item.source === 'local' && item.file) {
        blob = item.file;
      } else if (item.source === 'uploadthing' && item.uploadThingFile?.key) {
        if (!accessToken) {
          throw new Error('Cloud backup media requires an authenticated session');
        }
        // Signed URLs expire, so this is deliberately not written to the
        // long-lived `cache` map the way a blob URL would be.
        return await uploadthingService.getSignedUrl(accessToken, item.uploadThingFile.key);
      } else if (item.source === 'dropbox') {
        if (!accessToken) {
          throw new Error('Dropbox media requires an authenticated session');
        }
        const response = await fetch(`/api/dropbox/temporary-link?id=${encodeURIComponent(item.id)}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-access-token': accessToken,
          },
        });
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`Failed to fetch Dropbox link (${response.status}): ${text || 'unknown error'}`);
        }
        const payload = await response.json().catch(() => ({}));
        const link = typeof payload?.link === 'string' ? payload.link : '';
        if (!link) {
          throw new Error('Dropbox temporary link response was empty.');
        }
        const directUrl = toDropboxRawUrl(link);
        cache.set(item.id, directUrl);
        return directUrl;
      } else if (item.source === 'drive' && accessToken) {
        // Check if this is a HEIC/HEIF file (browsers don't support these natively)
        const isHeic = item.name.toLowerCase().endsWith('.heic') || 
                       item.name.toLowerCase().endsWith('.heif') ||
                       item.mimeType === 'image/heic' || 
                       item.mimeType === 'image/heif' ||
                       item.mimeType === 'image/heic-sequence' ||
                       item.mimeType === 'image/heif-sequence';
        
        // For HEIC files, use proxy endpoint (no CORS issues, fast)
        if (isHeic) {
          try {
            // Calculate optimal width (for grid view, use 512px as default)
            const width = calculateThumbnailWidth(512, false);
            
            // Use proxy endpoint - no CORS, no sequential retries
            const thumbUrl = await getThumbnailUrl(item.id, width, accessToken);
            
            // Fetch the blob from the object URL
            const response = await fetch(thumbUrl);
            blob = await response.blob();
            
            console.log(`HEIC thumbnail loaded via proxy (width: ${width})`);
          } catch (proxyError: any) {
            captureSentryException(proxyError, { fileId: item.id, isThumbnailProxy: true, mimeType: item.mimeType });
            console.warn('HEIC thumbnail proxy failed, falling back to conversion:', proxyError);
            
            // Fallback: Only convert if absolutely necessary (not during grid browsing)
            // This should rarely happen if proxy is working correctly
            try {
              const response = await fetch(`https://www.googleapis.com/drive/v3/files/${item.id}?alt=media`, {
                headers: { 'Authorization': `Bearer ${accessToken}` },
                signal: controller.signal
              });

              if (!response.ok) {
                const heicFetchError = new Error(`HEIC file cannot be fetched. Drive API returned status ${response.status}.`);
                captureSentryException(heicFetchError, {
                  fileId: item.id,
                  statusCode: response.status,
                  isThumbnailProxy: true,
                  mimeType: item.mimeType,
                });
                throw heicFetchError;
              }
              
              const heicBlob = await response.blob();
              const bytes = await heicBlob.arrayBuffer();
              const { blob: convertedBlob } = await convertHeicSingleFlight({
                imageId: item.id,
                bytes,
                maxSize: 1024,
              });

              blob = convertedBlob;

              console.log('HEIC converted to JPEG (fallback, worker)');
            } catch (conversionError: any) {
              console.error('HEIC conversion failed:', conversionError);
              
              // Check if it's a browser environment error
              const isBrowserError = conversionError.message?.includes('browser environment') ||
                                    conversionError.message?.includes('window is not defined') ||
                                    conversionError.message?.includes('Worker');
              
              const isFormatError = conversionError.code === 2 || 
                                   conversionError.message?.includes('format not supported') ||
                                   conversionError.message?.includes('ERR_LIBHEIF');
              
              if (isBrowserError) {
                // Browser environment issue - this shouldn't happen in production, but handle gracefully
                console.warn('HEIC conversion unavailable due to browser environment issue');
                throw new Error('HEIC conversion is not available in this environment. Please use a supported browser.');
              }
              
              const errorMsg = isFormatError
                ? 'HEIC file format is not supported. Please convert this file to JPEG or PNG format.'
                : conversionError.message || 'HEIC file cannot be displayed. The file may need to be converted to a web-compatible format.';
              throw new Error(errorMsg);
            }
          }
        } else {
          // Normal file - use media API
          const response = await fetch(`https://www.googleapis.com/drive/v3/files/${item.id}?alt=media`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
            signal: controller.signal
          });

          if (!response.ok) {
            const fetchError = new Error(`Fetch failed: ${response.status}`);
            captureSentryException(fetchError, {
              fileId: item.id,
              statusCode: response.status,
              isThumbnailProxy: false,
              mimeType: item.mimeType,
            });
            throw fetchError;
          }
          blob = await response.blob();
        }
        
        // Fix mime type if drive returns generic octet-stream but we know it's video/image
        if (item.mimeType && blob.type === 'application/octet-stream') {
          blob = new Blob([blob], { type: item.mimeType });
        }
      } else {
        throw new Error("Cannot load item: Missing source or credentials");
      }

      const url = URL.createObjectURL(blob);
      cache.set(item.id, url);
      return url;

    } finally {
      activeFetches.delete(item.id);
    }
  },

  /**
   * Preloads an item into the cache silently.
   */
  preload: (item: MediaItem, accessToken: string | null) => {
    if (cache.has(item.id) || activeFetches.has(item.id)) return;
    
    // We catch errors silently for preload
    mediaCache.load(item, accessToken).catch(e => {
      if (e.name !== 'AbortError') console.warn("Preload failed", e);
    });
  },

  /**
   * Cleans up old cache entries to free memory.
   * Keeps the current item and a buffer around it.
   */
  prune: (keepIds: string[]) => {
    const keepSet = new Set(keepIds);
    
    for (const [id, url] of cache.entries()) {
      if (!keepSet.has(id)) {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
        cache.delete(id);
      }
    }

    // Also cancel fetches for items we no longer care about
    for (const [id, controller] of activeFetches.entries()) {
      if (!keepSet.has(id)) {
        controller.abort();
        activeFetches.delete(id);
      }
    }
  },
  
  has: (id: string) => cache.has(id),

  /**
   * Load processed images for PRD-compliant delivery pipeline
   * Returns processed images in multiple formats and sizes
   * Uses serialized processing queue to prevent UI blocking
   */
  loadProcessedImages: async (
    item: MediaItem,
    accessToken: string | null,
    signal?: AbortSignal
  ): Promise<ProcessedImage[]> => {
    if (!item) throw new Error("No item provided");

    // Check if processed images exist in storage
    const hasProcessed = await imageStorageService.hasProcessedImages(item);
    if (hasProcessed) {
      const stored = await imageStorageService.getProcessedImages(item);
      if (stored.length > 0) {
        console.log(`Using cached processed images for ${item.name}`);
        return stored;
      }
    }

    // Process with serialization and cancellation support
    return processImageWithQueue(item.id, async (processingSignal) => {
      // Combine signals (cancel if either is aborted)
      let combinedSignal: AbortSignal = processingSignal;
      let cleanupAbortListeners: (() => void) | null = null;
      if (signal) {
        const combinedController = new AbortController();
        const forwardAbort = () => combinedController.abort();

        if (signal.aborted || processingSignal.aborted) {
          combinedController.abort();
        } else {
          signal.addEventListener('abort', forwardAbort);
          processingSignal.addEventListener('abort', forwardAbort);
          cleanupAbortListeners = () => {
            signal.removeEventListener('abort', forwardAbort);
            processingSignal.removeEventListener('abort', forwardAbort);
          };
        }

        combinedSignal = combinedController.signal;
      }

      try {
        console.log(`Processing image: ${item.name}`);

        // First, get the source blob
        let sourceBlob: Blob;

        if (item.source === 'local' && item.file) {
          sourceBlob = item.file;
        } else {
          if (item.source !== 'drive' || !accessToken) {
            throw new Error("Cannot load item: Missing source or credentials");
          }

          // For HEIC files, fetch original bytes for worker conversion
          const isHeic = item.name.toLowerCase().endsWith('.heic') ||
                         item.name.toLowerCase().endsWith('.heif') ||
                         item.mimeType === 'image/heic' ||
                         item.mimeType === 'image/heif';

          if (isHeic) {
            const response = await fetch(`https://www.googleapis.com/drive/v3/files/${item.id}?alt=media`, {
              headers: { 'Authorization': `Bearer ${accessToken}` },
              signal: combinedSignal
            });

            if (!response.ok) {
              throw new Error(`Failed to fetch HEIC: ${response.status}`);
            }

            sourceBlob = await response.blob();
          } else {
            // Non-HEIC: fetch directly
            const response = await fetch(`https://www.googleapis.com/drive/v3/files/${item.id}?alt=media`, {
              headers: { 'Authorization': `Bearer ${accessToken}` },
              signal: combinedSignal
            });

            if (!response.ok) {
              const fetchError = new Error(`Fetch failed: ${response.status}`);
              captureSentryException(fetchError, {
                fileId: item.id,
                statusCode: response.status,
                isThumbnailProxy: false,
                mimeType: item.mimeType,
              });
              throw fetchError;
            }

            sourceBlob = await response.blob();
          }
        }

        // Check if cancelled before processing
        if (combinedSignal.aborted) {
          throw new Error('Processing cancelled');
        }

        // Process the image (HEIC conversion runs in worker, serialized by queue)
        const { images, metadata } = await imageProcessingService.processImage(
          item,
          sourceBlob,
          accessToken
        );

        // Check if cancelled after processing
        if (combinedSignal.aborted) {
          throw new Error('Processing cancelled');
        }

        // Store processed images
        await imageStorageService.saveProcessedImages(item, images, getSourceVersion(item));
        await imageStorageService.saveImageMetadata(item, metadata);

        console.log(`Processed ${images.length} image variants for ${item.name}`);
        return images;
      } finally {
        cleanupAbortListeners?.();
      }
    });
  },

  /**
   * Cancel processing for an item
   */
  cancelProcessing: (itemId: string) => {
    cancelProcessing(itemId);
    cancelActiveHeicJob();
  },
};
